import type { EventTimeRange } from "@/features/events/constants/event-time-ranges";
import type { ScheduledJob, ScheduledJobState } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import {
  humanizeScheduledJobId,
  isScheduledJobOverdue,
} from "./scheduled-job-display";

/** Health buckets matching the badge ranking on the table. */
export type ScheduledJobHealthFilter =
  | "healthy"
  | "failing"
  | "overdue"
  | "untracked";

export interface ScheduledJobsViewFilters {
  range: EventTimeRange;
  /** Free-text over id, humanized id, description, eventType. */
  q?: string;
  state?: ScheduledJobState;
  health?: ScheduledJobHealthFilter;
  eventType?: string;
}

export function scheduledJobsHaveActiveFilters(
  filters: Pick<ScheduledJobsViewFilters, "q" | "state" | "health" | "eventType">,
): boolean {
  return (
    (filters.q != null && filters.q.trim().length > 0) ||
    filters.state != null ||
    filters.health != null ||
    filters.eventType != null
  );
}

/**
 * Badge-aligned health kind for filtering. Failure streak outranks overdue,
 * which outranks untracked.
 */
export function getScheduledJobHealthFilterKind(
  job: ScheduledJob,
  now: number,
  overdueGraceMs: number | undefined,
  unhealthyAfterFailures: number,
): ScheduledJobHealthFilter {
  if (job.health && job.health.consecutiveFailures > 0) {
    // Amber vs red is display-only; both count as "failing" for the filter.
    void unhealthyAfterFailures;
    return "failing";
  }
  if (isScheduledJobOverdue(job, now, overdueGraceMs)) return "overdue";
  if (!job.health) return "untracked";
  return "healthy";
}

export function filterScheduledJobs(
  jobs: readonly ScheduledJob[],
  filters: ScheduledJobsViewFilters,
  options: {
    now: number;
    overdueGraceMs?: number;
    unhealthyAfterFailures: number;
  },
): ScheduledJob[] {
  const query = filters.q?.trim().toLowerCase() ?? "";

  return jobs.filter((job) => {
    if (filters.state != null && job.state !== filters.state) return false;

    if (filters.eventType != null && job.eventType !== filters.eventType) return false;

    if (filters.health != null) {
      const kind = getScheduledJobHealthFilterKind(
        job,
        options.now,
        options.overdueGraceMs,
        options.unhealthyAfterFailures,
      );
      if (kind !== filters.health) return false;
    }

    if (query.length > 0) {
      const haystack = [
        job.id,
        humanizeScheduledJobId(job.id),
        job.description ?? "",
        job.eventType ?? "",
        job.name,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    return true;
  });
}

/** Distinct event types present in the loaded list, sorted. */
export function listScheduledJobEventTypes(jobs: readonly ScheduledJob[]): string[] {
  const set = new Set<string>();
  for (const job of jobs) {
    if (job.eventType) set.add(job.eventType);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
